import Component from '@glimmer/component';
import { service } from '@ember/service';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import {
  isColour,
  lighten,
  opacify,
} from '@linc-technologies/linced-common/utils/colour';
import type School from '@linc-technologies/linced-common/models/school';
import type ColoursService from '@linc-technologies/linced-common/services/colours';
import type OptionsService from '@linc-technologies/linced-common/services/options';

type GenderMethod = 'genderMale' | 'genderFemale' | 'genderNeutral';

// GENDERS gives the custom property name and the colours service method of
// each gender colour.
const GENDERS: [string, GenderMethod][] = [
  ['--gender-color-male', 'genderMale'],
  ['--gender-color-female', 'genderFemale'],
  ['--gender-color-neutral', 'genderNeutral'],
];

// SHADES gives the suffix and the lighten amount of each gender shade.
const SHADES: [string, number][] = [
  ['-dark', -25],
  ['-darker', -50],
  ['-light', 25],
  ['-lighter', 50],
];

// PROGRESS gives the judgement of the progress colours 1 to 5.
const PROGRESS = [
  ProgressionJudgement.WELL_BELOW,
  ProgressionJudgement.BELOW,
  ProgressionJudgement.AT,
  ProgressionJudgement.ABOVE,
  ProgressionJudgement.WELL_ABOVE,
];

const progressAlpha = '0.3';

interface DynamicStylesSignature {
  Args: { school: School | null | undefined };
}

/**
 * dynamic-styles gives the gender and progress colours of a school as CSS
 * custom properties on :root. The linced-dynamic-styles stylesheet has the
 * rules that read them, and the default gender colours. This style element
 * comes after that stylesheet, thus the school colours replace the defaults.
 * The options must be loaded first: caregivers load them in the route.
 */
export default class DynamicStyles extends Component<DynamicStylesSignature> {
  @service declare colours: ColoursService;
  @service declare options: OptionsService;

  get css(): string {
    const { school } = this.args;
    if (!school || !this.options.loaded) {
      return '';
    }

    let css = ':root{';
    for (const [name, method] of GENDERS) {
      const colour = this.colours[method](school);
      if (isColour(colour)) {
        css += `${name}:${colour};`;
        for (const [suffix, amount] of SHADES) {
          css += `${name}${suffix}:${lighten(colour, amount)};`;
        }
      }
    }
    PROGRESS.forEach((judgement, index) => {
      const colour = this.colours.progress(school, judgement);
      if (isColour(colour)) {
        const name = `--progress-colour-${index + 1}`;
        css += `${name}:${colour};`;
        css += `${name}-bg:${opacify(colour, progressAlpha)};`;
        css += `${name}-text:${this.colours.calculateTextColour(colour)};`;
      }
    });
    return `${css}}`;
  }

  <template>
    {{! This component exists to write a style element: the school colours must come after the linced-dynamic-styles stylesheet. }}
    {{! template-lint-disable no-forbidden-elements }}
    <style class="dynamic-styles">
      {{this.css}}
    </style>
  </template>
}
